import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Handshake, Plus, Link2 } from "lucide-react";
import { AddCompanyDrawer } from "@/components/add/AddCompanyDrawer";
import { AttributeExistingDialog } from "./AttributeExistingDialog";
import { useCoiDetail } from "@/hooks/useCoiDetail";
import { todayIso } from "@shared/originationByPath";

/**
 * The quick actions that carry the whole habit. Touches are logged through the
 * record page's own Log activity form instead.
 *  New prospect from this COI: opens Add Prospect with the source already attached.
 *  Attribute an existing prospect: credit this COI for a prospect already in the CRM.
 *  Log referral sent: work we sent their way.
 */
export function CoiQuickActions({ coi }: { coi: { id: string; name: string; firmName: string | null } }) {
  const { referralSent } = useCoiDetail(coi.id);
  const [sentOpen, setSentOpen] = useState(false);
  const [sentTitle, setSentTitle] = useState("");
  const [sentNote, setSentNote] = useState("");
  const [sentOn, setSentOn] = useState(todayIso());
  const [newProspectOpen, setNewProspectOpen] = useState(false);
  const [attributeOpen, setAttributeOpen] = useState(false);

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1 border-dashed" onClick={() => setNewProspectOpen(true)}><Plus className="w-3 h-3" /> New prospect from this COI</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1 border-dashed" onClick={() => setAttributeOpen(true)}><Link2 className="w-3 h-3" /> Attribute an existing prospect</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1 border-dashed" onClick={() => { setSentOpen(true); setSentTitle(""); setSentNote(""); setSentOn(todayIso()); }}><Handshake className="w-3 h-3" /> Log referral sent to them</Button>
      </div>

      <Dialog open={sentOpen} onOpenChange={setSentOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Referral sent to {coi.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">What</Label><Input value={sentTitle} onChange={(e) => setSentTitle(e.target.value)} placeholder="Buy-side referral, deal counsel for Redbud" /></div>
            <div className="space-y-1"><Label className="text-xs">When</Label><Input type="date" value={sentOn} onChange={(e) => setSentOn(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Note</Label><Input value={sentNote} onChange={(e) => setSentNote(e.target.value)} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSentOpen(false)}>Cancel</Button>
            <Button disabled={!sentTitle.trim() || referralSent.isPending} onClick={() => referralSent.mutate({ title: sentTitle.trim(), note: sentNote, occurredOn: sentOn }, { onSuccess: () => { setSentOpen(false); toast.success("Logged"); }, onError: (e: any) => toast.error(e.message) })}>Log</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AttributeExistingDialog open={attributeOpen} onOpenChange={setAttributeOpen} coi={{ id: coi.id, name: coi.name }} />
      <AddCompanyDrawer
        open={newProspectOpen} onOpenChange={setNewProspectOpen} preselectedCategory="prospects"
        initialOrigination={{ sourceType: "coi_referral", contactId: coi.id, occurredOn: todayIso() }}
        lockedSource={{ sourceType: "coi_referral", contactId: coi.id, contactName: coi.name, firmName: coi.firmName }}
      />
    </>
  );
}
